import type {
  GitHubInstallationConnection,
  GitHubRepositoryConnection,
  GitHubRepositoryUserConnections,
  GitHubUserConnection,
  ProjectGitHubRepositoryConnection,
  SlackUserLinkConnection,
  SlackWorkspaceConnection,
  TaskGitHubLinkConnection,
} from '@/lib/api/integrations.api'
import { calendarKeys } from '@/hooks/queries/useCalendarQuery'
import { integrationsApi } from '@/lib/api/integrations.api'
import {
  reportProviderConnectFailure,
  startProviderRedirect,
} from '@/lib/utils/oauth-connect'
import { useOrganizationStore } from '@/store/organization.store'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type {
  SlackChannelRouteSummary,
  SlackWorkspaceSettings,
} from '@scrambl/shared'
import {
  CreateIntegrationConnectionDto,
  IntegrationConnection,
  IntegrationProviderConfig,
  IntegrationScope,
  QuickBooksCustomer,
  UpdateIntegrationConnectionDto,
} from '@scrambl/shared'

interface QueryOptions {
  enabled?: boolean
}

// Query keys
export const integrationKeys = {
  all: ['integrations'] as const,
  available: (scope?: IntegrationScope) =>
    [...integrationKeys.all, 'available', scope] as const,
  organization: (orgId: string) =>
    [...integrationKeys.all, 'organization', orgId] as const,
  user: (orgId: string) => [...integrationKeys.all, 'user', orgId] as const,
  connection: (id: string) =>
    [...integrationKeys.all, 'connection', id] as const,
  syncLogs: (connectionId: string) =>
    [...integrationKeys.all, 'sync-logs', connectionId] as const,
  syncConfigs: (connectionId: string) =>
    [...integrationKeys.all, 'sync-configs', connectionId] as const,
  githubInstallations: (orgId: string) =>
    [...integrationKeys.all, 'github-installations', orgId] as const,
  githubUserConnections: (orgId: string) =>
    [...integrationKeys.all, 'github-user-connections', orgId] as const,
  githubRepositoryUserConnections: (orgId: string, repositoryId: string) =>
    [
      ...integrationKeys.all,
      'github-repository-user-connections',
      orgId,
      repositoryId,
    ] as const,
  githubRepositories: (orgId: string) =>
    [...integrationKeys.all, 'github-repositories', orgId] as const,
  githubProjectRepositories: (orgId: string, projectId: string) =>
    [
      ...integrationKeys.all,
      'github-project-repositories',
      orgId,
      projectId,
    ] as const,
  githubTaskLinks: (orgId: string, taskId: string) =>
    [...integrationKeys.all, 'github-task-links', orgId, taskId] as const,
  slackWorkspace: (orgId: string) =>
    [...integrationKeys.all, 'slack-workspace', orgId] as const,
  slackChannels: (orgId: string) =>
    [...integrationKeys.all, 'slack-channels', orgId] as const,
  slackChannelRoutes: (orgId: string) =>
    [...integrationKeys.all, 'slack-channel-routes', orgId] as const,
  slackLink: (orgId: string, slackTeamId?: string) =>
    [
      ...integrationKeys.all,
      'slack-link',
      orgId,
      slackTeamId ?? 'current',
    ] as const,
}
// Available integrations
export function useAvailableIntegrations(
  scope?: IntegrationScope,
  options?: QueryOptions,
) {
  return useQuery<IntegrationProviderConfig[]>({
    queryKey: integrationKeys.available(scope),
    queryFn: () => integrationsApi.getAvailableIntegrations(scope),
    enabled: options?.enabled ?? true,
  })
}

// Organization connections
export function useOrganizationConnections(options?: QueryOptions) {
  const { currentOrganization } = useOrganizationStore()
  const orgId = currentOrganization?.id || 'none'

  return useQuery<IntegrationConnection[]>({
    queryKey: integrationKeys.organization(orgId),
    queryFn: () => integrationsApi.getOrganizationConnections(),
    enabled: (options?.enabled ?? true) && !!currentOrganization?.id,
  })
}

// User connections
export function useUserConnections(options?: QueryOptions) {
  const { currentOrganization } = useOrganizationStore()
  const orgId = currentOrganization?.id || 'none'

  return useQuery<IntegrationConnection[]>({
    queryKey: integrationKeys.user(orgId),
    queryFn: async () => {
      const result = await integrationsApi.getUserConnections()
      return result
    },
    enabled: (options?.enabled ?? true) && !!currentOrganization?.id,
  })
}

// Single connection
export function useConnection(id: string) {
  return useQuery<IntegrationConnection>({
    queryKey: integrationKeys.connection(id),
    queryFn: () => integrationsApi.getConnection(id),
    enabled: !!id,
  })
}

// Sync logs
export function useSyncLogs(connectionId: string, limit?: number) {
  return useQuery({
    queryKey: integrationKeys.syncLogs(connectionId),
    queryFn: () => integrationsApi.getSyncLogs(connectionId, limit),
    enabled: !!connectionId,
  })
}

// Sync configurations
export function useSyncConfigurations(connectionId: string) {
  return useQuery({
    queryKey: integrationKeys.syncConfigs(connectionId),
    queryFn: () => integrationsApi.getSyncConfigurations(connectionId),
    enabled: !!connectionId,
  })
}

// Mutations
export function useCreateConnection() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateIntegrationConnectionDto) =>
      integrationsApi.createConnection(data),
    onSuccess: () => {
      // Invalidate all integration queries to refresh the list
      queryClient.invalidateQueries({ queryKey: integrationKeys.all })
    },
  })
}

export function useUpdateConnection() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: UpdateIntegrationConnectionDto
    }) => integrationsApi.updateConnection(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: integrationKeys.connection(id),
      })
      queryClient.invalidateQueries({ queryKey: integrationKeys.all })
    },
  })
}

export function useDeleteConnection() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => integrationsApi.deleteConnection(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.all })
    },
  })
}

export function useTestConnection() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => integrationsApi.testConnection(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: integrationKeys.connection(id),
      })
    },
  })
}

export function useUpdateSyncConfiguration() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      connectionId,
      configId,
      updates,
    }: {
      connectionId: string
      configId: string
      updates: any
    }) =>
      integrationsApi.updateSyncConfiguration(connectionId, configId, updates),
    onSuccess: (_, { connectionId }) => {
      queryClient.invalidateQueries({
        queryKey: integrationKeys.syncConfigs(connectionId),
      })
    },
  })
}

// QuickBooks specific hooks
export function useTriggerQuickBooksSync() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      connectionId,
      entityTypes,
    }: {
      connectionId: string
      entityTypes?: string[]
    }) => integrationsApi.quickbooks.triggerSync(connectionId, entityTypes),
    onSuccess: (_, { connectionId }) => {
      queryClient.invalidateQueries({
        queryKey: integrationKeys.syncLogs(connectionId),
      })
      queryClient.invalidateQueries({
        queryKey: integrationKeys.connection(connectionId),
      })
    },
  })
}

export function useQuickBooksCustomers(connectionId: string) {
  return useQuery<QuickBooksCustomer[]>({
    queryKey: [...integrationKeys.connection(connectionId), 'customers'],
    queryFn: () => integrationsApi.quickbooks.getCustomers(connectionId),
    enabled: !!connectionId,
  })
}

export function useQuickBooksItems(connectionId: string) {
  return useQuery({
    queryKey: [...integrationKeys.connection(connectionId), 'items'],
    queryFn: () => integrationsApi.quickbooks.getItems(connectionId),
    enabled: !!connectionId,
  })
}

export function useQuickBooksAccounts(connectionId: string) {
  return useQuery({
    queryKey: [...integrationKeys.connection(connectionId), 'accounts'],
    queryFn: () => integrationsApi.quickbooks.getAccounts(connectionId),
    enabled: !!connectionId,
  })
}

export function useQuickBooksTimeActivities(
  connectionId: string,
  startDate?: string,
  endDate?: string,
) {
  return useQuery({
    queryKey: [
      ...integrationKeys.connection(connectionId),
      'time-activities',
      startDate,
      endDate,
    ],
    queryFn: () =>
      integrationsApi.quickbooks.getTimeActivities(
        connectionId,
        startDate,
        endDate,
      ),
    enabled: !!connectionId,
  })
}

export function useExportTimeEntriesToQuickBooks() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      connectionId,
      timeEntryIds,
      options,
    }: {
      connectionId: string
      timeEntryIds: string[]
      options?: {
        billableStatus?: 'Billable' | 'NotBillable'
        customerId?: string
        itemId?: string
      }
    }) =>
      integrationsApi.quickbooks.exportTimeEntries(
        connectionId,
        timeEntryIds,
        options,
      ),
    onSuccess: (_, { connectionId }) => {
      queryClient.invalidateQueries({
        queryKey: integrationKeys.syncLogs(connectionId),
      })
    },
  })
}

// The single organization-scoped QuickBooks connection, if one is set up.
// Invoice surfaces need the connection ID but should not care how it is found.
export function useQuickBooksConnection() {
  const { data: connections, isLoading } = useOrganizationConnections()

  const connection = connections?.find(
    (candidate: any) =>
      String(candidate.type).toUpperCase() === 'QUICKBOOKS' &&
      candidate.isActive !== false,
  )

  return { connection, connectionId: connection?.id as string | undefined, isLoading }
}

export function useQuickBooksInvoiceLink(
  connectionId: string | undefined,
  invoiceId: string | undefined,
) {
  return useQuery<{
    externalId: string
    lastSyncAt: string
    url: string
  } | null>({
    queryKey: [
      ...integrationKeys.connection(connectionId ?? 'none'),
      'invoices',
      invoiceId,
      'link',
    ],
    queryFn: () =>
      integrationsApi.quickbooks.getInvoiceLink(connectionId!, invoiceId!),
    enabled: !!connectionId && !!invoiceId,
  })
}

export function useExportInvoicesToQuickBooks() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      connectionId,
      invoiceIds,
      options,
    }: {
      connectionId: string
      invoiceIds: string[]
      options?: { defaultItemId?: string }
    }) =>
      integrationsApi.quickbooks.exportInvoices(
        connectionId,
        invoiceIds,
        options,
      ),
    onSuccess: (_, { connectionId, invoiceIds }) => {
      queryClient.invalidateQueries({
        queryKey: integrationKeys.syncLogs(connectionId),
      })
      // Refresh each invoice's link so a successful push shows immediately.
      invoiceIds.forEach(invoiceId => {
        queryClient.invalidateQueries({
          queryKey: [
            ...integrationKeys.connection(connectionId),
            'invoices',
            invoiceId,
            'link',
          ],
        })
      })
    },
  })
}

export function useQuickBooksCustomerLink(
  connectionId: string,
  entityId: string,
) {
  return useQuery<{
    customerId: string
    customer: QuickBooksCustomer | null
  } | null>({
    queryKey: [
      ...integrationKeys.connection(connectionId),
      'entities',
      entityId,
      'customer-link',
    ],
    queryFn: () =>
      integrationsApi.quickbooks.getEntityCustomerLink(connectionId, entityId),
    enabled: !!connectionId && !!entityId,
  })
}

export function useQuickBooksCustomerLinks(connectionId: string) {
  return useQuery<
    Array<{
      entityId: string
      entityName: string
      customerId: string
      customer: QuickBooksCustomer | null
      lastSyncAt?: Date
    }>
  >({
    queryKey: [...integrationKeys.connection(connectionId), 'customer-links'],
    queryFn: () => integrationsApi.quickbooks.getCustomerLinks(connectionId),
    enabled: !!connectionId,
  })
}

export function useLinkQuickBooksCustomer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      connectionId,
      entityId,
      customerId,
    }: {
      connectionId: string
      entityId: string
      customerId: string
    }) =>
      integrationsApi.quickbooks.linkEntityCustomer(
        connectionId,
        entityId,
        customerId,
      ),
    onSuccess: (_, { connectionId, entityId }) => {
      queryClient.invalidateQueries({
        queryKey: [
          ...integrationKeys.connection(connectionId),
          'entities',
          entityId,
          'customer-link',
        ],
      })
      queryClient.invalidateQueries({
        queryKey: [
          ...integrationKeys.connection(connectionId),
          'customer-links',
        ],
      })
    },
  })
}

export function useUnlinkQuickBooksCustomer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      connectionId,
      entityId,
    }: {
      connectionId: string
      entityId: string
    }) =>
      integrationsApi.quickbooks.unlinkEntityCustomer(connectionId, entityId),
    onSuccess: (_, { connectionId, entityId }) => {
      queryClient.invalidateQueries({
        queryKey: [
          ...integrationKeys.connection(connectionId),
          'entities',
          entityId,
          'customer-link',
        ],
      })
      queryClient.invalidateQueries({
        queryKey: [
          ...integrationKeys.connection(connectionId),
          'customer-links',
        ],
      })
    },
  })
}

// Google Calendar specific hooks
export function useGoogleCalendarAuth() {
  return useMutation({
    mutationFn: (orgSlug?: string) =>
      integrationsApi.googleCalendar.getAuthUrl(orgSlug),
    onSuccess: data => startProviderRedirect(data.authUrl),
    onError: reportProviderConnectFailure('Google Calendar'),
    // Says why, in place of the generic mutation toast in queryClient.ts.
    meta: { silent: true },
  })
}

export function useGoogleCalendarCallback() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ code, state }: { code: string; state: string }) =>
      integrationsApi.googleCalendar.handleCallback(code, state),
    onSuccess: () => {
      // Invalidate all integration queries to refresh the list
      queryClient.invalidateQueries({ queryKey: integrationKeys.all })
    },
  })
}

export function useGoogleCalendarConnection() {
  return useQuery({
    queryKey: [...integrationKeys.all, 'google-calendar', 'connection'],
    queryFn: () => integrationsApi.googleCalendar.getConnection(),
    retry: false,
  })
}

export function useGoogleCalendarDisconnect() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (connectionId?: string) =>
      integrationsApi.googleCalendar.disconnect(connectionId),
    onSuccess: () => {
      // Invalidate all integration queries to refresh the list
      queryClient.invalidateQueries({ queryKey: integrationKeys.all })
    },
  })
}

export function useGoogleCalendars() {
  return useQuery({
    queryKey: [...integrationKeys.all, 'google-calendar', 'calendars'],
    queryFn: () => integrationsApi.googleCalendar.getCalendars(),
    // 404s when the integration isn't connected — don't retry
    retry: false,
  })
}

/** Every connected Google account with its calendars. */
export function useGoogleCalendarAccounts() {
  return useQuery({
    queryKey: [...integrationKeys.all, 'google-calendar', 'accounts'],
    queryFn: () => integrationsApi.googleCalendar.getAccounts(),
    retry: false,
  })
}

export function useUpdateGoogleCalendarSelection() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      selectedCalendarIds,
      connectionId,
    }: {
      selectedCalendarIds: string[]
      connectionId?: string
    }) =>
      integrationsApi.googleCalendar.updateCalendarSelection(
        selectedCalendarIds,
        connectionId,
      ),
    onSuccess: () => {
      // Invalidate calendars to refresh the selection status
      queryClient.invalidateQueries({
        queryKey: [...integrationKeys.all, 'google-calendar'],
      })
      // Also invalidate calendar events to force refresh with only selected calendars
      // This removes events from deselected calendars from the UI
      queryClient.invalidateQueries({ queryKey: calendarKeys.all })
    },
  })
}

export function useGoogleCalendarSync() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (connectionId?: string) =>
      integrationsApi.googleCalendar.syncCalendar(connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...integrationKeys.all, 'google-calendar'],
      })
    },
  })
}

export function useGoogleCalendarSyncStatus() {
  return useQuery({
    queryKey: [...integrationKeys.all, 'google-calendar', 'sync-status'],
    queryFn: () => integrationsApi.googleCalendar.getSyncStatus(),
  })
}

// Outlook Calendar specific hooks
export function useOutlookCalendarAuth() {
  return useMutation({
    mutationFn: (orgSlug?: string) =>
      integrationsApi.outlookCalendar.getAuthUrl(orgSlug),
    onSuccess: data => startProviderRedirect(data.authUrl),
    onError: reportProviderConnectFailure('Outlook Calendar'),
    meta: { silent: true },
  })
}

export function useOutlookCalendarCallback() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ code, state }: { code: string; state: string }) =>
      integrationsApi.outlookCalendar.handleCallback(code, state),
    onSuccess: () => {
      // Invalidate all integration queries to refresh the list
      queryClient.invalidateQueries({ queryKey: integrationKeys.all })
    },
  })
}

export function useOutlookCalendarConnection() {
  return useQuery({
    queryKey: [...integrationKeys.all, 'outlook-calendar', 'connection'],
    queryFn: () => integrationsApi.outlookCalendar.getConnection(),
    retry: false,
  })
}

export function useOutlookCalendarDisconnect() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (connectionId?: string) =>
      integrationsApi.outlookCalendar.disconnect(connectionId),
    onSuccess: () => {
      // Invalidate all integration queries to refresh the list
      queryClient.invalidateQueries({ queryKey: integrationKeys.all })
    },
  })
}

export function useOutlookCalendars() {
  return useQuery({
    queryKey: [...integrationKeys.all, 'outlook-calendar', 'calendars'],
    queryFn: () => integrationsApi.outlookCalendar.getCalendars(),
    // 404s when the integration isn't connected — don't retry
    retry: false,
  })
}

/** Every connected Microsoft account with its calendars. */
export function useOutlookCalendarAccounts() {
  return useQuery({
    queryKey: [...integrationKeys.all, 'outlook-calendar', 'accounts'],
    queryFn: () => integrationsApi.outlookCalendar.getAccounts(),
    retry: false,
  })
}

export function useUpdateOutlookCalendarSelection() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      selectedCalendarIds,
      connectionId,
    }: {
      selectedCalendarIds: string[]
      connectionId?: string
    }) =>
      integrationsApi.outlookCalendar.updateCalendarSelection(
        selectedCalendarIds,
        connectionId,
      ),
    onSuccess: () => {
      // Invalidate calendars to refresh the selection status
      queryClient.invalidateQueries({
        queryKey: [...integrationKeys.all, 'outlook-calendar'],
      })
      // Also invalidate calendar events to force refresh with only selected calendars
      // This removes events from deselected calendars from the UI
      queryClient.invalidateQueries({ queryKey: calendarKeys.all })
    },
  })
}

export function useOutlookCalendarSync() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (connectionId?: string) =>
      integrationsApi.outlookCalendar.syncCalendar(connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...integrationKeys.all, 'outlook-calendar'],
      })
    },
  })
}

export function useOutlookCalendarSyncStatus() {
  return useQuery({
    queryKey: [...integrationKeys.all, 'outlook-calendar', 'sync-status'],
    queryFn: () => integrationsApi.outlookCalendar.getSyncStatus(),
  })
}

export interface ZoomAuthOptions {
  orgSlug?: string
  returnPath?: string
}

export function useZoomAuth() {
  return useMutation({
    mutationFn: (options?: ZoomAuthOptions) =>
      integrationsApi.zoom.getAuthUrl(options),
    onSuccess: data => startProviderRedirect(data.authUrl),
    onError: reportProviderConnectFailure('Zoom'),
    meta: { silent: true },
  })
}

export function useZoomCallback() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ code, state }: { code: string; state: string }) =>
      integrationsApi.zoom.handleCallback(code, state),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.all })
    },
  })
}

export function useZoomConnection(orgSlug?: string) {
  return useQuery({
    queryKey: [...integrationKeys.all, 'zoom', 'connection', orgSlug ?? 'none'],
    queryFn: () => integrationsApi.zoom.getConnection(orgSlug),
    enabled: !!orgSlug,
    retry: false,
  })
}

export function useZoomDisconnect() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (orgSlug?: string) => integrationsApi.zoom.disconnect(orgSlug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.all })
    },
  })
}

export function useGitHubInstall() {
  return useMutation({
    mutationFn: (returnPath?: string) =>
      integrationsApi.github.getInstallUrl(returnPath),
    onSuccess: data => startProviderRedirect(data.url),
    onError: reportProviderConnectFailure('GitHub'),
    meta: { silent: true },
  })
}

// Per-user GitHub identities (user-to-server tokens for branch/PR creation)
export function useGitHubUserConnections(options?: QueryOptions) {
  const { currentOrganization } = useOrganizationStore()
  const orgId = currentOrganization?.id || 'none'

  return useQuery<GitHubUserConnection[]>({
    queryKey: integrationKeys.githubUserConnections(orgId),
    queryFn: () => integrationsApi.github.listUserConnections(),
    enabled: (options?.enabled ?? true) && !!currentOrganization?.id,
  })
}

export function useConnectGitHubUser() {
  return useMutation({
    mutationFn: (input?: { returnPath?: string; login?: string }) =>
      integrationsApi.github.getUserConnectUrl(input?.returnPath, input?.login),
    onSuccess: data => startProviderRedirect(data.url),
    onError: reportProviderConnectFailure('GitHub'),
    meta: { silent: true },
  })
}

export function useDisconnectGitHubUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (connectionId: string) =>
      integrationsApi.github.disconnectUser(connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.all })
    },
  })
}

export function useGitHubInstallations(options?: QueryOptions) {
  const { currentOrganization } = useOrganizationStore()
  const orgId = currentOrganization?.id || 'none'

  return useQuery<GitHubInstallationConnection[]>({
    queryKey: integrationKeys.githubInstallations(orgId),
    queryFn: () => integrationsApi.github.listInstallations(),
    enabled: (options?.enabled ?? true) && !!currentOrganization?.id,
  })
}

export function useGitHubRepositories(options?: QueryOptions) {
  const { currentOrganization } = useOrganizationStore()
  const orgId = currentOrganization?.id || 'none'

  return useQuery<GitHubRepositoryConnection[]>({
    queryKey: integrationKeys.githubRepositories(orgId),
    queryFn: () => integrationsApi.github.listRepositories(),
    enabled: (options?.enabled ?? true) && !!currentOrganization?.id,
  })
}

// Live branch names for a connected repo — powers branch pickers. Cached a
// minute; new branches appear on the next open.
export function useGitHubRepositoryBranches(
  repositoryId?: string,
  options?: QueryOptions,
) {
  const { currentOrganization } = useOrganizationStore()
  const orgId = currentOrganization?.id || 'none'

  return useQuery({
    queryKey: [
      ...integrationKeys.all,
      'github-repo-branches',
      orgId,
      repositoryId,
    ],
    queryFn: () => integrationsApi.github.listRepositoryBranches(repositoryId!),
    enabled:
      (options?.enabled ?? true) && !!currentOrganization?.id && !!repositoryId,
    staleTime: 60_000,
  })
}

export function useGitHubRepositoryUserConnections(
  repositoryId?: string,
  options?: QueryOptions,
) {
  const { currentOrganization } = useOrganizationStore()
  const orgId = currentOrganization?.id || 'none'

  return useQuery<GitHubRepositoryUserConnections>({
    queryKey: integrationKeys.githubRepositoryUserConnections(
      orgId,
      repositoryId ?? 'none',
    ),
    queryFn: () =>
      integrationsApi.github.listRepositoryUserConnections(repositoryId!),
    enabled:
      (options?.enabled ?? true) && !!currentOrganization?.id && !!repositoryId,
    staleTime: 60_000,
  })
}

export function useSyncGitHubRepositories() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (installationId: string) =>
      integrationsApi.github.syncRepositories(installationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.all })
    },
  })
}

export function useDisconnectGitHubInstallation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (installationId: string) =>
      integrationsApi.github.disconnectInstallation(installationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.all })
    },
  })
}

export function useProjectGitHubRepositories(
  projectId: string,
  options?: QueryOptions,
) {
  const { currentOrganization } = useOrganizationStore()
  const orgId = currentOrganization?.id || 'none'

  return useQuery<ProjectGitHubRepositoryConnection[]>({
    queryKey: integrationKeys.githubProjectRepositories(orgId, projectId),
    queryFn: () => integrationsApi.github.listProjectRepositories(projectId),
    enabled:
      (options?.enabled ?? true) && !!currentOrganization?.id && !!projectId,
  })
}

export function useLinkProjectGitHubRepository() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      projectId,
      data,
    }: {
      projectId: string
      data: Parameters<typeof integrationsApi.github.linkProjectRepository>[1]
    }) => integrationsApi.github.linkProjectRepository(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.all })
    },
  })
}

export function useUpdateProjectGitHubRepository() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      projectId,
      repositoryId,
      data,
    }: {
      projectId: string
      repositoryId: string
      data: Parameters<typeof integrationsApi.github.updateProjectRepository>[2]
    }) =>
      integrationsApi.github.updateProjectRepository(
        projectId,
        repositoryId,
        data,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.all })
    },
  })
}

export function useUnlinkProjectGitHubRepository() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      projectId,
      repositoryId,
    }: {
      projectId: string
      repositoryId: string
    }) =>
      integrationsApi.github.unlinkProjectRepository(projectId, repositoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.all })
    },
  })
}

export function useTaskGitHubLinks(taskId: string, options?: QueryOptions) {
  const { currentOrganization } = useOrganizationStore()
  const orgId = currentOrganization?.id || 'none'

  return useQuery<TaskGitHubLinkConnection[]>({
    queryKey: integrationKeys.githubTaskLinks(orgId, taskId),
    queryFn: () => integrationsApi.github.listTaskLinks(taskId),
    enabled:
      (options?.enabled ?? true) && !!currentOrganization?.id && !!taskId,
  })
}

export function useCreateGitHubBranch() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      taskId,
      data,
    }: {
      taskId: string
      data: Parameters<typeof integrationsApi.github.createBranch>[1]
    }) => integrationsApi.github.createBranch(taskId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.all })
    },
  })
}

export function useCreateGitHubPullRequest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      taskId,
      data,
    }: {
      taskId: string
      data: Parameters<typeof integrationsApi.github.createPullRequest>[1]
    }) => integrationsApi.github.createPullRequest(taskId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.all })
    },
  })
}

export function useSlackWorkspace(options?: QueryOptions) {
  const { currentOrganization } = useOrganizationStore()
  const orgId = currentOrganization?.id || 'none'

  return useQuery<SlackWorkspaceConnection | null>({
    queryKey: integrationKeys.slackWorkspace(orgId),
    queryFn: async () => {
      const result = await integrationsApi.slack.getWorkspace()
      return result.workspace
    },
    enabled: (options?.enabled ?? true) && !!currentOrganization?.id,
  })
}

export function useSlackInstall() {
  return useMutation({
    mutationFn: (returnPath?: string) =>
      integrationsApi.slack.getInstallUrl(returnPath),
    onSuccess: data => startProviderRedirect(data.url),
    onError: reportProviderConnectFailure('Slack'),
    meta: { silent: true },
  })
}

export function useDisconnectSlackWorkspace() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => integrationsApi.slack.disconnectWorkspace(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.all })
    },
  })
}

export function useUpdateSlackWorkspaceSettings() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganizationStore()
  const orgId = currentOrganization?.id || 'none'

  return useMutation({
    mutationFn: (settings: SlackWorkspaceSettings) =>
      integrationsApi.slack.updateWorkspaceSettings(settings),
    onSuccess: result => {
      queryClient.setQueryData(
        integrationKeys.slackWorkspace(orgId),
        result.workspace,
      )
    },
  })
}

// Channel picker for channel-alert routes. Only fetched while the dialog is
// open; Slack's conversations.list is rate-limited, so keep it fresh-ish.
export function useSlackChannels(options?: QueryOptions) {
  const { currentOrganization } = useOrganizationStore()
  const orgId = currentOrganization?.id || 'none'

  return useQuery({
    queryKey: integrationKeys.slackChannels(orgId),
    queryFn: () => integrationsApi.slack.listChannels(),
    enabled: (options?.enabled ?? true) && !!currentOrganization?.id,
    staleTime: 60_000,
    retry: false,
  })
}

export function useSlackChannelRoutes(options?: QueryOptions) {
  const { currentOrganization } = useOrganizationStore()
  const orgId = currentOrganization?.id || 'none'

  return useQuery<SlackChannelRouteSummary[]>({
    queryKey: integrationKeys.slackChannelRoutes(orgId),
    queryFn: async () => {
      const result = await integrationsApi.slack.listChannelRoutes()
      return result.routes
    },
    enabled: (options?.enabled ?? true) && !!currentOrganization?.id,
  })
}

export function useCreateSlackChannelRoute() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganizationStore()
  const orgId = currentOrganization?.id || 'none'

  return useMutation({
    mutationFn: (
      body: Parameters<typeof integrationsApi.slack.createChannelRoute>[0],
    ) => integrationsApi.slack.createChannelRoute(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: integrationKeys.slackChannelRoutes(orgId),
      })
    },
  })
}

export function useUpdateSlackChannelRoute() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganizationStore()
  const orgId = currentOrganization?.id || 'none'

  return useMutation({
    mutationFn: ({
      routeId,
      ...body
    }: {
      routeId: string
      projectId?: string | null
      eventTypes?: string[]
      enabled?: boolean
    }) => integrationsApi.slack.updateChannelRoute(routeId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: integrationKeys.slackChannelRoutes(orgId),
      })
    },
  })
}

export function useDeleteSlackChannelRoute() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganizationStore()
  const orgId = currentOrganization?.id || 'none'

  return useMutation({
    mutationFn: (routeId: string) =>
      integrationsApi.slack.deleteChannelRoute(routeId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: integrationKeys.slackChannelRoutes(orgId),
      })
    },
  })
}

export function useTestSlackChannelRoute() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganizationStore()
  const orgId = currentOrganization?.id || 'none'

  return useMutation({
    mutationFn: (routeId: string) =>
      integrationsApi.slack.testChannelRoute(routeId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: integrationKeys.slackChannelRoutes(orgId),
      })
    },
  })
}

export function useSlackUserLink(slackTeamId?: string, options?: QueryOptions) {
  const { currentOrganization } = useOrganizationStore()
  const orgId = currentOrganization?.id || 'none'

  return useQuery<SlackUserLinkConnection | null>({
    queryKey: integrationKeys.slackLink(orgId, slackTeamId),
    queryFn: async () => {
      const result = await integrationsApi.slack.getLink(slackTeamId)
      return result.link
    },
    enabled: (options?.enabled ?? true) && !!currentOrganization?.id,
  })
}

export function useCompleteSlackUserLink() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      state,
      preferredOpenTarget,
    }: {
      state: string
      preferredOpenTarget?: 'auto' | 'app' | 'web'
    }) => integrationsApi.slack.completeLink(state, preferredOpenTarget),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.all })
    },
  })
}

export function useDisconnectSlackUserLink() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (slackTeamId?: string) =>
      integrationsApi.slack.disconnectLink(slackTeamId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.all })
    },
  })
}
